import * as signatureActions from '@literax/modules/documents/store/signature/signature.actions';

import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  MergedSignRequest,
  WorkspaceSelectors,
  selectCurrentParticipant,
} from '@literax/modules/documents/store/workspace/workspace.selectors';
import { Observable, merge } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  selectEmailError,
  selectImageError,
  selectNameError,
  selectTokenErrors,
} from '@literax/modules/documents/store/signature/signature.selector';
import {
  selectEmailFreeError,
  selectFreeImageError,
  selectFreeTokenErrors,
  selectNameFreeError,
} from '@literax/store/free-signature/free-signature.selector';

import { ESignatureVerificationTypeId } from '@literax/enums/document.enum';
import { IAppState } from '@literax/store';
import { ICreateSignatureRequest } from '@literax/models/signature.model';
import {
  ILegalPerson
} from '@literax/models/participant.model';
import { WorkspaceActions } from '@literax/modules/documents/store/workspace/workspace.actions';
import { map } from 'rxjs/operators';
import { environment } from '@environments/environment';

const HINT_SHOW_COUNT_KEY = 'hsck';

@UntilDestroy()
@Component({
  selector: 'literax-signature-form',
  templateUrl: './signature-form.component.html',
  styleUrls: ['./signature-form.component.scss'],
})
export class SignatureFormComponent implements OnInit, OnDestroy {
  @Input() signatureBodyRequest: ICreateSignatureRequest;
  @Input() documentId: number;
  @Input() tokenData: string;
  @Input() nameDocument: string;
  @Input() typeSignatureSession: boolean;
  @Input() plusSignature = false;
  @Input() otpSignature = false;
  @Input() isToValidate = false;
  showCompanyAndRole = false;
  signatureForm: FormGroup;
  permittedFiles = ['.pdf', '.jpg', '.png'];
  tokenErrorUser$ = this.store.pipe(
    untilDestroyed(this),
    select(selectTokenErrors)
  );
  imagesErrorUser$ = this.store.pipe(
    untilDestroyed(this),
    select(selectImageError)
  );
  imagesErrorFree$ = this.store.pipe(
    untilDestroyed(this),
    select(selectFreeImageError)
  );
  tokenErrorFree$ = this.store.pipe(
    untilDestroyed(this),
    select(selectFreeTokenErrors)
  );
  nameError$ = this.store.pipe(untilDestroyed(this), select(selectNameError));
  emailError$ = this.store.pipe(untilDestroyed(this), select(selectEmailError));
  nameFreError$ = this.store.pipe(
    untilDestroyed(this),
    select(selectNameFreeError)
  );
  emailFreeError$ = this.store.pipe(
    untilDestroyed(this),
    select(selectEmailFreeError)
  );
  tokenErrors$ = merge(this.tokenErrorUser$, this.tokenErrorFree$);
  imagesErrors$ = merge(this.imagesErrorUser$, this.imagesErrorFree$);
  namesErrors$ = merge(this.nameError$, this.nameFreError$);
  emailsErros$ = merge(this.emailError$, this.emailFreeError$);

  @Output() cancel: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() disableSignButton: EventEmitter<boolean> =
    new EventEmitter<boolean>(true);
  @Input() makeSignature: Observable<ICreateSignatureRequest>;

  currentParticipant$ = this.store.pipe(
    untilDestroyed(this),
    select(selectCurrentParticipant)
  );
  signerId: number;

  signatureVerificationType$: Observable<ESignatureVerificationTypeId> =
    this.store.pipe(
      untilDestroyed(this),
      select(WorkspaceSelectors.selectSignatureVerificationType)
    );
  signatureVerificationType: ESignatureVerificationTypeId;
  eSignatureVerificationTypeId = ESignatureVerificationTypeId;
  passwordVisible = false;
  showHint = false;

  /**
   * Privacy link of the SMS consent legend shown under every SMS code field. Only the QA
   * environment sets it, so the legend stays hidden everywhere else.
   */
  smsConsentPrivacyUrl = environment.smsConsentPrivacyUrl;
  verificationTokenLabel: Observable<string>;

  smsCounter = 15;
  smsTimer: any;

  /** True when the document's verification type is email-and-SMS (FR-015a). */
  isDualChannel = false;

  /** The step the participant is currently on: 1 (email) or 2 (SMS) (FR-015b). */
  currentStep: 1 | 2 = 1;

  /** The channel the current step's code arrives through, mirroring {@link currentStep}. */
  currentChannel: 'email' | 'sms' = 'email';

  /** True once both dual-channel steps are confirmed and the document may be signed. */
  dualChannelReadyToSign = false;

  /** Address the step-1 code is delivered to, shown so the signer can check it (FR-039). */
  stepEmail = '';

  /** Number the step-2 code is delivered to, shown so the signer can check it (FR-039). */
  stepPhoneNumber = '';

  /**
   * Whether a code has already been issued for the step the participant is on (FR-041a).
   *
   * Derived rather than requested. Step 2 is always reached with a code already issued, because
   * confirming step 1 issues it automatically (FR-006). Step 1 is always email (A-003), and email
   * carries no send limit (FR-023), so starting a fresh load at `false` costs nothing — at worst
   * the participant requests a code they already had. That is why the status operation needs no
   * new field for this.
   */
  stepCodeIssued = false;

  /**
   * Whether the status read now in flight was triggered by a send that succeeded.
   *
   * The status operation reports the step but not whether a code is outstanding, and it is read
   * both on load and after every successful send (FR-016c). Without this distinction the read that
   * follows a send concludes "step 1, so nothing issued" and hides the confirm action the signer
   * was about to use — which is exactly what it did.
   */
  private codeIssuedByLastSend = false;

  /**
   * Where the current dual-channel step's code is delivered, for the hint under the field and the
   * notification that announces the send (FR-039).
   */
  get stepDestination(): string {
    return this.currentChannel === 'sms' ? this.stepPhoneNumber : this.stepEmail;
  }

  /**
   * Hint key for the current dual-channel step. Dual-channel documents get their own keys rather
   * than the shared `AUTENTHICATION_CODE_TEXT*` ones, which carry no destination placeholder and
   * are still shown, unchanged, by every single-channel document (FR-038).
   */
  get stepHintKey(): string {
    return this.currentChannel === 'sms'
      ? 'FORM.SIGNATURE.AUTENTHICATION_CODE_TEXT_DUAL_SMS'
      : 'FORM.SIGNATURE.AUTENTHICATION_CODE_TEXT_DUAL_EMAIL';
  }

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<IAppState>,
    private actions$: Actions,
    private cdr: ChangeDetectorRef,
    private modal: NzModalService,
    private translateService: TranslateService
  ) {
  }

  ngOnDestroy(): void {
    // `untilDestroyed` unsubscribes the observables, but the resend countdown is a plain interval —
    // left running it keeps ticking against a destroyed component.
    clearInterval(this.smsTimer);
  }

  ngOnInit(): void {
    
    this.createForm();
    this.setCompanies();
    if (this.plusSignature) {
      (this.signatureForm.get('electronicSignature') as FormGroup).addControl(
        'twoFactorConfirmationToken',
        new FormControl('', Validators.required)
      );
      this.signatureVerificationType$.subscribe((signatureVerificationType) => {
        this.signatureVerificationType = signatureVerificationType;
        this.isDualChannel =
          signatureVerificationType ===
          ESignatureVerificationTypeId.EMAIL_AND_SMS;

        if (this.isDualChannel) {
          // FR-015b/A-008: resume with the participant's real progress on a fresh load or a
          // different device, instead of defaulting to the email step.
          this.store.dispatch(
            signatureActions.loadTwoFactorTokenStatus({
              documentId: this.documentId,
              signRequestId: this.signerId,
              token: this.typeSignatureSession ? null : this.tokenData,
            })
          );
        }

        if (signatureVerificationType === ESignatureVerificationTypeId.TOKEN) {
          this.store.dispatch(WorkspaceActions.getVerificationTypes());
          this.verificationTokenLabel = this.store.pipe(
            untilDestroyed(this),
            select((state) => state.workspace.signatureVerificationTypes),
            map(
              (verificationType) =>
                verificationType.find(
                  (vType) => vType.id === signatureVerificationType
                )?.name
            )
          );
        }

        if (signatureVerificationType === ESignatureVerificationTypeId.EMAIL) {
          this.validateHint();
        }
      });
      this.actions$
        .pipe(
          untilDestroyed(this),
          ofType(
            signatureActions.twoFactorTokenError,
            signatureActions.twoFactorToken,
            signatureActions.twoFactorTokenSuccess
          )
        )
        .subscribe((action) => {
          const control: AbstractControl = (
            this.signatureForm.get('electronicSignature') as FormGroup
          ).get('twoFactorConfirmationToken');
          if (
            (this.signatureVerificationType ===
              ESignatureVerificationTypeId.SMS ||
              (this.isDualChannel && this.currentChannel === 'sms')) &&
            action.type ===
              signatureActions.ESignatureActions.twoFactorTokenError
          ) {
            control.setErrors({
              smsError: {
                failed: true,
                message: action.payload.message,
              },
            });
            clearInterval(this.smsTimer);
            this.smsCounter = 15;
          }

          // FR-016c: a resend can resolve to a different step than the one on screen (e.g. the
          // backend falls back a stale SMS step to a fresh email code past the 60-minute window).
          // Re-read the real step so the form follows it instead of staying on the step the signer
          // was previously stuck on.
          //
          // FR-042: only on a dual-channel document. The status operation refuses anything else
          // with a 400 (FR-033), and nothing subscribes to the resulting error, so on a
          // single-channel document this was one wasted round trip per code request and a refusal
          // in the backend logs for a call that had no business being made.
          if (
            this.isDualChannel &&
            action.type ===
              signatureActions.ESignatureActions.twoFactorTokenSuccess
          ) {
            // FR-041a: the send went through, so a code is out for whatever step the read below
            // reports — including the fallback case this re-read exists for, where the backend
            // answers with a fresh email code for step 1.
            this.codeIssuedByLastSend = true;

            this.store.dispatch(
              signatureActions.loadTwoFactorTokenStatus({
                documentId: this.documentId,
                signRequestId: this.signerId,
                token: this.typeSignatureSession ? null : this.tokenData,
              })
            );
          }
        });

      // Dual-channel (email + SMS) OTP confirmation: FR-015c — a confirm response moves the
      // participant to the next step (or to ready-to-sign) in place, with no navigation/reload.
      this.actions$
        .pipe(
          untilDestroyed(this),
          ofType(
            signatureActions.confirmTwoFactorTokenSuccess,
            signatureActions.confirmTwoFactorTokenError
          )
        )
        .subscribe((action) => {
          const control: AbstractControl = (
            this.signatureForm.get('electronicSignature') as FormGroup
          ).get('twoFactorConfirmationToken');

          if (
            action.type ===
            signatureActions.ESignatureActions.confirmTwoFactorTokenSuccess
          ) {
            if (action.payload.nextStep === 2) {
              this.currentStep = 2;
              this.currentChannel = 'sms';
              // FR-041a: step 2 arrives with its code already sent, so the form opens that step on
              // the confirm action rather than asking the participant to request what is en route.
              this.stepCodeIssued = true;
              // Confirming the email step makes the backend send the step-2 SMS immediately, so the
              // spacing clock is already running there — start it here too, or the resend button
              // stays enabled and the signer's first click is rejected for being too soon.
              this.startSmsSpacingCountdown();
            }
            this.dualChannelReadyToSign = action.payload.readyToSign;
            control.setValue('');
            control.setErrors(null);
            control.markAsPristine();
            control.markAsUntouched();
            if (this.dualChannelReadyToSign) {
              control.clearValidators();
              control.updateValueAndValidity();
            }
            this.disableSignButton.emit(
              !this.signatureForm.valid || !this.dualChannelReadyToSign
            );
          } else {
            control.setErrors({
              confirmError: {
                failed: true,
                message: this.buildConfirmErrorMessage(action.payload),
              },
            });

            // Confirming is not atomic: the backend commits the step it just confirmed before it
            // issues the next step's code, so an error here does not mean the step was not taken.
            // When the automatic step-2 SMS issuance fails (send limit reached, or the provider
            // refused the message), step 1 is already confirmed and re-submitting that same email
            // code can only fail — the row is spent. Re-read the participant's real step so the
            // form follows the backend instead of staying on a step that has already passed; the
            // load handler below applies it, and the error above stays visible so they can see why
            // the SMS never arrived and use the resend button. Harmless on the ordinary wrong- or
            // expired-code errors, where the step read back is simply the one they are already on.
            this.store.dispatch(
              signatureActions.loadTwoFactorTokenStatus({
                documentId: this.documentId,
                signRequestId: this.signerId,
                token: this.typeSignatureSession ? null : this.tokenData,
              })
            );
          }

          // This subscription runs on the NgRx Actions stream, not from a template-bound event or
          // an @Input change, so nothing here otherwise tells Angular this component's view needs
          // rechecking — currentStep/currentChannel update on the instance immediately, but the
          // step label can be left showing the stale step until something unrelated elsewhere
          // happens to trigger change detection.
          this.cdr.markForCheck();
        });

      // FR-015b/A-008: apply the participant's real current step once it's read back on load,
      // in place of the email/step-1 default this component starts with.
      this.actions$
        .pipe(
          untilDestroyed(this),
          ofType(signatureActions.loadTwoFactorTokenStatusSuccess)
        )
        .subscribe((action) => {
          const control: AbstractControl = (
            this.signatureForm.get('electronicSignature') as FormGroup
          ).get('twoFactorConfirmationToken');

          this.currentStep = action.payload.currentStep === 2 ? 2 : 1;
          this.currentChannel = this.currentStep === 2 ? 'sms' : 'email';
          this.dualChannelReadyToSign = action.payload.readyToSign;
          // FR-041a: a code is out when this read follows a successful send, or when the step read
          // back is 2, whose code is issued automatically. Only a plain load of step 1 means none —
          // the conservative reading, and a free one, since email has no send limit.
          this.stepCodeIssued =
            this.codeIssuedByLastSend || this.currentStep === 2;
          this.codeIssuedByLastSend = false;

          if (this.dualChannelReadyToSign) {
            control.clearValidators();
            control.updateValueAndValidity();
          }

          this.disableSignButton.emit(
            !this.signatureForm.valid || !this.dualChannelReadyToSign
          );
        });
    }
    this.makeSignature.pipe(untilDestroyed(this)).subscribe((data) => {
      this.onSubmit(data);
    });
  }

  validateHint() {
    const hintsCount = localStorage.getItem(HINT_SHOW_COUNT_KEY);
    if (hintsCount === null) {
      localStorage.setItem(HINT_SHOW_COUNT_KEY, JSON.stringify({ count: 1 }));
    } else {
      const count = JSON.parse(hintsCount).count + 1;
      localStorage.setItem(HINT_SHOW_COUNT_KEY, JSON.stringify({ count }));
      if (count >= 10) {
        this.showHint = true;
      }
    }
  }

  createForm() {
    this.signatureForm = this.formBuilder.group({
      electronicSignature: this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      name: [null, [Validators.required]],
      signatureImage: [
        null,
        this.otpSignature ? [] : [Validators.required],
      ],
      moralInfo: this.formBuilder.array([]),
      }),
    });
    this.signatureForm.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.disableSignButton.emit(
          !this.signatureForm.valid ||
            (this.isDualChannel && !this.dualChannelReadyToSign)
        );
      });
  }

  setCompanies() {
    const items = this.signatureForm
      .get('electronicSignature')
      .get('moralInfo') as FormArray;

    this.currentParticipant$
      .pipe(untilDestroyed(this))
      .subscribe((currentParticipant: MergedSignRequest) => {
        this.signerId = currentParticipant.signRequestId;

        // FR-039: the destination of each dual-channel step. `GetAsync` already returns
        // `phoneNumber` for an EmailAndSms document, so nothing new is requested here.
        this.stepEmail = currentParticipant.email ?? '';
        this.stepPhoneNumber = currentParticipant.phoneNumber ?? '';
        if (this.instanceOfLegalPerson(currentParticipant)) {
          this.showCompanyAndRole = true;
          const form = this.buildSignRequests();
          form.get('companyName').setValidators(Validators.required);
          form.get('companyName').updateValueAndValidity();
          form.get('role').setValidators(Validators.required);
          form.get('role').updateValueAndValidity();
          items.push(form);
        } else {
          this.showCompanyAndRole = false;
          items.clear();
        }
      });
    this.signatureForm.patchValue(this.signatureBodyRequest);
  }

  instanceOfLegalPerson(data: any): data is ILegalPerson {
    return 'companyName' in data;
  }

  buildSignRequests(): FormGroup {
    return this.formBuilder.group({
      companyName: [null],
      role: [null],
    });
  }

  onSubmit(data: ICreateSignatureRequest) {
    const mergeSignature = {
      ...this.signatureBodyRequest.electronicSignature,
      ...this.signatureForm.value.electronicSignature,
    };
    const signData = { ...mergeSignature };

    const haveId = signData?.idImage ? { idImage: signData.idImage } : {};
    const haveIdImageFront = signData?.idImageFront ? { idImageFront: signData.idImageFront } : {};
    const haveIdImageBack = signData?.idImageBack ? { idImageBack: signData.idImageBack } : {};
    const haveLatitude = signData?.latitude ? { latitude: signData.latitude } : {};
    const haveLongitude = signData?.longitude ? { longitude: signData.longitude } : {};

    const SignatureBodyRequest: ICreateSignatureRequest = {
      electronicSignature: {
        name: signData.name,
        email: signData.email,
        twoFactorConfirmationToken: signData.twoFactorConfirmationToken,
        signatureImage: signData.signatureImage,
        ...haveId,
        ...haveIdImageFront,
        ...haveIdImageBack,
        ...haveLatitude,
        ...haveLongitude,
        companyName: signData.moralInfo[0]?.companyName,
        role: signData.moralInfo[0]?.role,
        isSignatureOtp: this.otpSignature,
      },
    };

    if (this.typeSignatureSession) {
      this.store.dispatch(
        signatureActions.registerSignature({
          payload: {
            documentId: this.documentId,
            signRequestId: this.signerId,
            signatureData: SignatureBodyRequest,
          },
        })
      );
    } else {
      this.store.dispatch(
        signatureActions.registerSignature({
          payload: {
            documentId: this.documentId,
            signRequestId: this.signerId,
            signatureData: SignatureBodyRequest,
            token: this.tokenData,
          },
        })
      );
    }
  }

  saveSignature(event: string) {
    const field = this.signatureForm
      .get('electronicSignature')
      .get('signatureImage');
    field.setValue(event);
    field.updateValueAndValidity();
  }

  preventDefault(e) {
    e.preventDefault();
  }

  get moralInfo(): FormArray {
    return this.signatureForm
      .get('electronicSignature')
      .get('moralInfo') as FormArray;
  }

  /**
   * Requests a code for the current step, asking first when doing so would waste one (FR-041d).
   *
   * Issuing a code invalidates the outstanding one of the same step (FR-012) and the field is not
   * cleared, so an accidental click used to leave the participant staring at a value that no longer
   * worked — and on the SMS step it also spent one send against the FR-018/FR-019 thresholds.
   * Single-channel documents keep dispatching straight away, unchanged (FR-041e).
   */
  twoFactorToken() {
    const typedCode = (
      this.signatureForm.get('electronicSignature') as FormGroup
    ).get('twoFactorConfirmationToken')?.value;

    if (this.isDualChannel && !!typedCode) {
      this.modal.confirm({
        nzTitle: this.translateService.instant(
          'FORM.SIGNATURE.RESEND_CONFIRM_TITLE'
        ),
        nzContent: this.translateService.instant(
          'FORM.SIGNATURE.RESEND_CONFIRM_MSG'
        ),
        nzOkText: this.translateService.instant(
          'FORM.SIGNATURE.RESEND_CONFIRM_OK'
        ),
        nzCancelText: this.translateService.instant(
          'FORM.SIGNATURE.RESEND_CONFIRM_CANCEL'
        ),
        nzCentered: true,
        nzOnOk: () => this.requestTwoFactorToken(),
      });

      return;
    }

    this.requestTwoFactorToken();
  }

  /** Dispatches the request and records that the current step now has a code out (FR-041a). */
  private requestTwoFactorToken() {
    const isSmsStep =
      this.signatureVerificationType === ESignatureVerificationTypeId.SMS ||
      (this.isDualChannel && this.currentChannel === 'sms');

    this.store.dispatch(
      signatureActions.twoFactorToken({
        documentId: this.documentId,
        signRequestId: this.signerId,
        token: this.typeSignatureSession ? null : this.tokenData,
        channel: this.isDualChannel ? this.currentChannel : undefined,
      })
    );

    this.stepCodeIssued = true;

    if (isSmsStep) {
      this.startSmsSpacingCountdown();
    }
  }

  /**
   * Starts the fifteen-second countdown that keeps the resend button disabled, matching the spacing
   * rule the backend enforces on SMS sends. Called from both places an SMS actually goes out: a
   * manual request through {@link twoFactorToken}, and the automatic step-2 issuance the backend
   * performs when a dual-channel participant confirms the email step. Without the second caller the
   * counter sat at 15 after the automatic send, leaving the button enabled and the signer's first
   * click guaranteed to bounce off the spacing rule.
   */
  private startSmsSpacingCountdown() {
    // Clearing first keeps a second call from running two intervals against the same counter, which
    // would drain it at double speed and re-enable the button early.
    clearInterval(this.smsTimer);
    this.smsCounter = 15;

    this.smsTimer = setInterval(() => {
      this.smsCounter--;
      if (this.smsCounter === 0) {
        clearInterval(this.smsTimer);
        this.smsCounter = 15;
      }
    }, 1000);
  }

  /**
   * Confirms the code entered for the participant's current dual-channel step (FR-008), separate
   * from the signing operation itself.
   */
  confirmTwoFactorToken() {
    const control = (
      this.signatureForm.get('electronicSignature') as FormGroup
    ).get('twoFactorConfirmationToken');

    this.store.dispatch(
      signatureActions.confirmTwoFactorToken({
        documentId: this.documentId,
        signRequestId: this.signerId,
        confirmationToken: control.value,
        token: this.typeSignatureSession ? null : this.tokenData,
      })
    );
  }

  /**
   * Builds the text shown against the confirmation-code field from a rejected confirmation (FR-035).
   *
   * A `422` from the validator carries the reason the signer needs under `fields`, while `message`
   * is only the generic heading above it — showing `message` alone is what left the form saying
   * "Uno o más errores han ocurrido." with the actual reason discarded. Every field entry is joined
   * because a future rule could add a second one; today there is only `confirmationToken`. A `400`
   * raised by the service layer carries no `fields`, so its `message` is already the real reason and
   * is used as-is.
   */
  private buildConfirmErrorMessage(payload: {
    message: string;
    fields?: Record<string, string[]>;
  }): string {
    const detail = Object.values(payload?.fields ?? {})
      .reduce((all: string[], messages) => all.concat(messages ?? []), [])
      .filter((message) => !!message);

    return detail.length ? detail.join(' ') : payload?.message;
  }
}
